import type { QuickView } from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user';

type UseQuickViewActionsResult = {
  /** The handler to update a quick view. */
  handleUpdateQuickView: (
    quickView: QuickView,
  ) => Promise<{ quickView: QuickView }>;
  /** The handler to duplicate a quick view. */
  handleDuplicateQuickView: (quickView: QuickView) => void;
  /** The handler to delete a quick view. */
  handleDeleteQuickView: (quickView: QuickView) => Promise<{ isDone: boolean }>;
  /** The handler to set or toggle a quick view's favorite state. */
  handleUpdateQuickViewFavoriteState: (
    quickView: QuickView,
    favorited?: boolean,
  ) => Promise<void>;
  /** The handler to transfer a quick view's ownership. */
  handleTransferQuickViewOwnership: (
    quickView: QuickView,
  ) => Promise<{ quickView: QuickView }>;
  /** The handler to set a quick view as the Analytics landing page. */
  handleToggleQuickViewAsLandingPage: (quickView: QuickView) => void;
};

export const useQuickViewActions = (): UseQuickViewActionsResult => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const navigate = useAppNavigate();
  const { user } = useCurrentUser();
  const storage = useServerStorage('user');
  const { landingPageId } = storage.data.analytics ?? {};

  const [deleteQuickView] = analyticsApi.useDeleteQuickViewMutation();
  const [updateQuickViewFavoriteState] =
    analyticsApi.useUpdateQuickViewFavoriteStateMutation();

  /** Handles updating a quick view. */
  const handleUpdateQuickView: UseQuickViewActionsResult['handleUpdateQuickView'] =
    async (quickView) => {
      return await dispatch(
        appActions.showModal({
          modal: {
            type: 'analytics:quickView',
            arg: {
              mode: 'update',
              quickView,
            },
          },
        }),
      );
    };

  /** Handles creating a new quick view based on an existing one. */
  const handleDuplicateQuickView: UseQuickViewActionsResult['handleDuplicateQuickView'] =
    async (quickView) => {
      const { quickView: createdQuickView } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'analytics:quickView',
            arg: {
              mode: 'duplicate',
              quickView,
            },
          },
        }),
      );

      navigate({
        path: '/analytics/dashboard/custom/:id',
        params: {
          path: {
            id: createdQuickView.id,
          },
        },
      });
    };

  /** Handles deleting a quick view. */
  const handleDeleteQuickView: UseQuickViewActionsResult['handleDeleteQuickView'] =
    async (quickView) => {
      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: `Delete "${quickView.name}"`,
              description: 'Are you sure you want to delete this dashboard?',
              confirm: {
                label: 'Delete',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        try {
          await deleteQuickView({
            id: quickView.id,
          });

          // If the deleted quick view was set as the landing page, remove it from the user storage.
          if (landingPageId === quickView.id) {
            storage.set({
              analytics: (draft) => {
                delete draft.landingPageId;
              },
            });
          }

          notify(
            {
              title: 'Success',
              text: sanitizeHtml(
                `"${quickView.name}" has been deleted.`,
                'plainText',
              ),
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );

          ampli.analyticsQuickViewDeleted({ shared: quickView.shared });

          return Promise.resolve({ isDone: true });
        } catch (error) {
          notify(
            {
              title: 'Error while deleting the dashboard.',
            },
            NotificationType.Error,
          );
        }
      }

      return Promise.resolve({ isDone: false });
    };

  /** Handles updating the favorite state of a quick view. */
  const handleUpdateQuickViewFavoriteState: UseQuickViewActionsResult['handleUpdateQuickViewFavoriteState'] =
    async (quickView, favorited = !quickView.favorited) => {
      try {
        await updateQuickViewFavoriteState({
          quickView,
          favorited,
        }).unwrap();

        ampli.analyticsQuickViewFavoriteToggled({ favorited });
      } catch (error) {
        notify(
          {
            title: `"${quickView.name}" could not be ${
              favorited ? 'favorited' : 'unfavorited'
            }.`,
          },
          NotificationType.Error,
        );
      }
    };

  /** Handles transferring the ownership of a quick view. */
  const handleTransferQuickViewOwnership: UseQuickViewActionsResult['handleTransferQuickViewOwnership'] =
    async (quickView) => {
      return await dispatch(
        appActions.showModal({
          modal: {
            type: 'analytics:quickView',
            arg: {
              mode: 'transferOwnership',
              quickView,
            },
          },
        }),
      );
    };

  const handleToggleQuickViewAsLandingPage: UseQuickViewActionsResult['handleToggleQuickViewAsLandingPage'] =
    async (quickView) => {
      const isLandingPage = landingPageId === quickView.id;

      // If not already favorited, set it as favorite when setting as landing page so it's also
      // easier to find in the sidebar.
      if (!isLandingPage && !quickView.favorited) {
        handleUpdateQuickViewFavoriteState(quickView, true);
      }

      storage.set({
        analytics: (draft) => {
          if (isLandingPage) {
            delete draft.landingPageId;

            ampli.analyticsQuickViewLandingPageUnset({
              shared: quickView.shared,
              isOwner: quickView.user.id === user.id,
            });
          } else {
            draft.landingPageId = quickView.id;

            ampli.analyticsQuickViewLandingPageSet({
              shared: quickView.shared,
              isOwner: quickView.user.id === user.id,
            });
          }
        },
      });

      notify(
        {
          title: 'Success',
          text: sanitizeHtml(
            `"${quickView.name}" has been ${
              isLandingPage ? 'removed' : 'set'
            } as your landing page.`,
            'plainText',
          ),
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );
    };

  return {
    handleUpdateQuickView,
    handleDuplicateQuickView,
    handleDeleteQuickView,
    handleUpdateQuickViewFavoriteState,
    handleTransferQuickViewOwnership,
    handleToggleQuickViewAsLandingPage,
  };
};
